import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/apiError';
import { AlertTriangle, CheckCircle2, KeyRound, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const input = "w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm";

/**
 * Cambiar la contrasena desde la cuenta, con la actual (entrega 54). El
 * servidor cierra las otras sesiones y le manda a esta un token nuevo.
 */
export default function CambiarContrasena() {
  const { token, actualizarToken } = useAuth();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cambiar = async (e) => {
    e.preventDefault();
    if (nueva !== repetida) { setAviso({ ok: false, texto: 'The new passwords do not match.' }); return; }
    setGuardando(true);
    setAviso(null);
    try {
      const r = await axios.post(
        `${API}/auth/change-password`,
        { current_password: actual, new_password: nueva },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      actualizarToken(r.data.access_token);
      setActual('');
      setNueva('');
      setRepetida('');
      setAviso({ ok: true, texto: 'Password changed. Any other device signed in to this account was signed out.' });
    } catch (err) {
      setAviso({ ok: false, texto: apiErrorMessage(err.response?.data, 'Could not change the password. Try again.') });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form
      onSubmit={cambiar}
      className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4"
      data-testid="change-password-card">
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-slate-500" />
        <h3 className="text-lg font-semibold">Change password</h3>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="space-y-1 text-sm">
          <span className="text-slate-500">Current password</span>
          <input type="password" autoComplete="current-password" value={actual}
            onChange={(e) => setActual(e.target.value)} required className={input} data-testid="current-password-input" />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-slate-500">New password (8+ characters)</span>
          <input type="password" autoComplete="new-password" value={nueva}
            onChange={(e) => setNueva(e.target.value)} required className={input} data-testid="new-password-input" />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-slate-500">Repeat the new password</span>
          <input type="password" autoComplete="new-password" value={repetida}
            onChange={(e) => setRepetida(e.target.value)} required className={input} data-testid="repeat-password-input" />
        </label>
      </div>
      {aviso && (
        <div
          className={`p-3 rounded-lg text-sm flex items-start gap-2 ${aviso.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
          data-testid="change-password-aviso">
          {aviso.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{aviso.texto}</span>
        </div>
      )}
      <Button type="submit" disabled={guardando} data-testid="change-password-submit">
        {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change password'}
      </Button>
    </form>
  );
}
